import { useTranslation } from 'react-i18next';
import type { RowData } from '@tanstack/react-table';

import { Button } from '@/components/atoms/Buttons';
import { SelectMenu } from '@/components/molecules/SelectMenu';

import type { DataTableInstance } from './features';

import styles from './DataTable.module.scss';

interface PaginationControlsProps<T extends RowData> {
    table: DataTableInstance<T>;
    pageSizeOptions?: number[];
    onPageSizeChange?: (pageSize: number) => void;
}

export default function PaginationControls<T extends RowData>({
    table,
    pageSizeOptions = [25, 50, 100],
    onPageSizeChange,
}: PaginationControlsProps<T>) {
    const { t } = useTranslation();
    const pageCount = table.getPageCount();
    const currentPage = table.state.pagination.pageIndex + 1;
    const pageSize = table.state.pagination.pageSize;
    const totalRows = table.getFilteredRowModel().rows.length;
    const currentPageRows = table.getRowModel().rows;
    const startRow = currentPageRows.length > 0 ? currentPage * pageSize - pageSize + 1 : 0;
    const endRow = currentPageRows.length > 0 ? Math.min(currentPage * pageSize, totalRows) : 0;

    return (
        <div className={styles.paginationControls}>
            <div className={styles.paginationInfo}>
                <span>
                    {t('organisms.dataTable.showingEntries', {
                        start: startRow,
                        end: endRow,
                        total: totalRows,
                    })}
                </span>
            </div>
            <div className={styles.paginationButtons}>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => table.setPageIndex(0)}
                    disabled={!table.getCanPreviousPage()}
                    className={styles.paginationButton}
                    trackingId="table_pagination_first">
                    {t('organisms.dataTable.first')}
                </Button>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => table.previousPage()}
                    disabled={!table.getCanPreviousPage()}
                    className={styles.paginationButton}
                    trackingId="table_pagination_previous">
                    {t('organisms.dataTable.previous')}
                </Button>
                <span className={styles.paginationPageInfo}>
                    {t('organisms.dataTable.pageOf', { page: currentPage, pages: pageCount || 1 })}
                </span>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => table.nextPage()}
                    disabled={!table.getCanNextPage()}
                    className={styles.paginationButton}
                    trackingId="table_pagination_next">
                    {t('organisms.dataTable.next')}
                </Button>
                <Button
                    variant="primary"
                    size="small"
                    onClick={() => table.setPageIndex(pageCount - 1)}
                    disabled={!table.getCanNextPage()}
                    className={styles.paginationButton}
                    trackingId="table_pagination_last">
                    {t('organisms.dataTable.last')}
                </Button>
            </div>
            <div className={styles.pageSizeSelector}>
                <SelectMenu
                    value={pageSize}
                    onChange={(size) => {
                        table.setPageSize(size);
                        onPageSizeChange?.(size);
                    }}
                    options={pageSizeOptions.map((size) => ({
                        value: size,
                        label: t('organisms.dataTable.perPage', { size }),
                    }))}
                    // The size the table has, even one a caller set outside the options.
                    renderValue={() => t('organisms.dataTable.perPage', { size: pageSize })}
                    size="small"
                />
            </div>
        </div>
    );
}
